import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import Modal from 'new/components/Modal';
import Button from 'new/components/Button/Button';
import {
  AddUserRequest,
  NewUser,
  CardId,
  PermissionType,
  SetPermissionsRequest,
} from 'new/modules/OfficeSettings/types';
import {
  useAddUser,
  useSavePermissions,
} from 'new/modules/OfficeSettings/hooks';
import { permissionsLabelMap } from 'new/modules/OfficeSettings/components/OfficeSettingsUsers';
import useActiveOffice from 'new/hooks/useActiveOffice';
import PermissionsCard from './PermissionsCard';
import ConfirmCoordinatorModal from './ConfirmCoordinatorModal';
import ConfirmSuperAdminModal from './ConfirmSuperAdminModal';
import AssignUserToLocationsGroupsModal from 'new/modules/OfficeSettings/components/AssignUserToLocationsGroupsModal';
import { ListItem } from 'new/components/ListMultiSelect';
import { useAssignUser } from 'new/modules/ManageUsers/hooks';
import { TenantStruct } from 'new/types/util';
import { TENANT } from 'new/tenants';

const StyledModal = styled(Modal)(({ theme }) => ({
  '.title': {
    textAlign: 'left',
    fontSize: '1.75rem',
    color: theme.palette.primary.dark,
  },

  '.modalContent': {
    width: 800,
  },

  '.buttonFooter': {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
}));

const standardPermissions: TenantStruct<string[]> = {
  vhq: [
    'Enroll & manage members',
    'View members',
    'Access, view, and print reports',
    'View & support CommunityCONNECT™ Groups',
  ],
  dhq: [
    'Enroll & manage members',
    'View members',
    'View & support BusinessCONNECT™ Employer Account',
    'View & support CommunityCONNECT™ Groups',
  ],
};

const adminPermissions: TenantStruct<string[]> = {
  vhq: [
    'All of the standard permissions',
    'Edit and change billing account details',
    'Create & edit plans',
    'Edit office profile',
    'Add practice locations',
    'Add and manage user accounts',
  ],
  dhq: [
    'Access, view, and print reports',
    'Edit and change billing account details',
    'Create & edit plans',
    'Order marketing materials',
    'Edit office profile',
    'Add practice locations',
    'Add and manage user accounts',
    'Invite businesses, establish discounts',
  ],
};

const permissionsList = [
  {
    name: 'Standard',
    cardId: 'dentist_standard' as CardId,
    permissions: standardPermissions[TENANT.id],
  },
  {
    name: 'Admin',
    cardId: 'office_admin' as CardId,
    isAdminCard: true,
    permissions: adminPermissions[TENANT.id],
  },
];

type User = {
  userId: number;
  isOwner: boolean;
  isFinanceCoordinator: boolean;
  user: {
    firstName: string;
    lastName: string;
    type: PermissionType;
  };
};

type Props = {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
  users: User[];
  resetAddForm?: () => void;
  activeUser?: User;
  newUser?: NewUser;
  isMultiLocation?: boolean;
};

const PermissionsModal: React.FC<Props> = ({
  open,
  onClose,
  activeUser,
  refetch,
  newUser,
  resetAddForm,
  users,
  isMultiLocation,
}) => {
  const {
    activeOfficeId,
    user,
    isSuperAdmin: isSuperAdminActiveOffice,
  } = useActiveOffice();
  const [selectedPermissionId, setSelectedPermissionId] =
    useState<PermissionType>();
  const [isPlanCoordinator, setIsPlanCoordinator] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isCoordinatorConfirmModalOpen, setIsCoordinatorConfirmModalOpen] =
    useState(false);
  const [isSuperAdminConfirmModalOpen, setIsSuperAdminConfirmModalOpen] =
    useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const { mutate: setPermissions, isLoading: isSettingPermissions } =
    useSavePermissions(activeOfficeId);
  const { mutate: addUser, isLoading: isAddingUser } =
    useAddUser(activeOfficeId);
  const {
    mutate: assignUser,
    reset: resetAssignUser,
    isSuccess: isAssignSuccess,
    isError: isAssignError,
  } = useAssignUser();

  const businessOwner = users.find((user) => user.isOwner);
  const planCoordinator = users.find((user) => user.isFinanceCoordinator);
  const isActiveUserPlanCoordinator = activeUser?.isFinanceCoordinator;

  const isUserPlanCoordinator =
    (user && user.data.userId === planCoordinator?.userId) || false;
  const isUserBusinessOwner =
    (user && user.data.userId === businessOwner?.userId) || false;
  const isUserSuperAdmin = isSuperAdminActiveOffice || false;
  const isOfficeAdmin = selectedPermissionId === 'office_admin';
  const displayName = newUser || activeUser?.user;

  const handleClose = () => {
    if (activeUser) {
      setSelectedPermissionId(activeUser.user.type);
      setIsPlanCoordinator(activeUser.isFinanceCoordinator);
      setIsSuperAdmin(activeUser.user.type === 'dentist');
    }
    setIsCoordinatorConfirmModalOpen(false);
    setIsAssignModalOpen(false);
    resetAssignUser();
    onClose();
  };

  const handleAddUser = (onSuccess?: (userId: number) => void) => {
    if (newUser) {
      const addUserRequest: AddUserRequest = {
        ...newUser,
        isPlanCoordinator: (isOfficeAdmin || isSuperAdmin) && isPlanCoordinator,
        type: isSuperAdmin
          ? 'dentist'
          : selectedPermissionId ?? 'dentist_standard',
        dentistInfoId: activeOfficeId,
        userId: null,
      };

      addUser(addUserRequest, {
        onSuccess: (data) => {
          if (onSuccess) {
            onSuccess?.(data.userId);
          } else {
            refetch();
            resetAddForm?.();
            handleClose();
          }
        },
      });
    }
  };

  const handleSubmit = () => {
    if (activeUser) {
      const permissionsRequest: SetPermissionsRequest = {
        userId: activeUser.userId,
        isPlanCoordinator: isOfficeAdmin && isPlanCoordinator,
        type:
          isSuperAdmin && selectedPermissionId != 'dentist_standard'
            ? 'dentist'
            : selectedPermissionId ?? 'dentist_standard',
        dentistInfoId: Number(activeOfficeId),
      };
      setPermissions(permissionsRequest, {
        onSuccess: () => {
          const close = () => {
            handleClose();
          };
          refetch();
          close();
        },
      });
    } else if (newUser) {
      handleAddUser();
    }
  };

  const handleAssignSubmit = (items: ListItem[]) => {
    handleAddUser((userId) => {
      const groups = items
        .filter((item) => item.type === 'group')
        .map((item) => Number(item.key));
      const locations = items
        .filter((item) => item.type !== 'group')
        .map((item) => Number(item.key));

      assignUser(
        { userId, groups, locations },
        {
          onSuccess: () => {
            resetAddForm?.();
          },
        },
      );
    });
  };

  const handleConfirm = () => {
    if (isPlanCoordinator && !isActiveUserPlanCoordinator && isOfficeAdmin) {
      setIsCoordinatorConfirmModalOpen(true);
    }
    // else if (isMultiLocation) {
    //   setIsAssignModalOpen(true);
    // }
    else {
      handleSubmit();
    }
  };

  const getIsFormDirty = () => {
    if (activeUser?.user.type) {
      return activeUser.user.type == 'dentist' &&
        selectedPermissionId == 'office_admin' &&
        isSuperAdmin &&
        activeUser.isFinanceCoordinator === isPlanCoordinator
        ? false
        : (!!selectedPermissionId &&
            (activeUser.isFinanceCoordinator !== isPlanCoordinator ||
              activeUser.user.type !== selectedPermissionId)) ||
            (activeUser.user.type === 'dentist' && !isSuperAdmin) ||
            (activeUser.user.type !== 'dentist' && isSuperAdmin);
    } else {
      return !!selectedPermissionId;
    }
  };

  useEffect(() => {
    if (
      activeUser?.user.type &&
      Object.keys(permissionsLabelMap).includes(activeUser.user.type)
    ) {
      setSelectedPermissionId(activeUser.user.type as PermissionType);
      setIsPlanCoordinator(activeUser.isFinanceCoordinator);
      setIsSuperAdmin(activeUser.user.type === 'dentist');
    }
  }, [activeUser?.user.type]);

  const checkIsSelected = (cardId: CardId) => {
    const checkSelected =
      cardId === selectedPermissionId ||
      (cardId === 'office_admin' && selectedPermissionId === 'dentist');
    return checkSelected;
  };

  let submitLabel = 'ADD USER';

  if (activeUser) {
    submitLabel = 'SAVE CHANGES';
  }

  // if (isMultiLocation) {
  //   submitLabel = 'ASSIGN LOCATIONS';
  // }

  const handleCloseAssign = () => {
    setIsAssignModalOpen(false);

    if (isAssignSuccess) {
      refetch();
      handleClose();
    }
  };

  return (
    <>
      <StyledModal
        open={open && !isCoordinatorConfirmModalOpen}
        onClose={handleClose}
        title={newUser ? 'Set Permissions' : 'Edit Permissions'}
      >
        {displayName && (displayName.firstName || displayName.lastName) && (
          <Typography>
            {displayName.firstName} {displayName.lastName} can:
          </Typography>
        )}
        {permissionsList.map((permissionsProps) => (
          <PermissionsCard
            key={permissionsProps.cardId}
            isSuperAdmin={isSuperAdmin}
            toggleIsSuperAdmin={() => {
              if (isSuperAdmin) {
                return setIsSuperAdmin(false);
              }

              setIsSuperAdminConfirmModalOpen(true);
            }}
            isSelected={checkIsSelected(permissionsProps.cardId)}
            setSelectedPermissionId={setSelectedPermissionId}
            isPlanCoordinator={
              permissionsProps.cardId === 'office_admin' && isPlanCoordinator
            }
            setIsPlanCoordinator={setIsPlanCoordinator}
            isUserPlanCoordinator={isUserPlanCoordinator}
            isUserBusinessOwner={isUserBusinessOwner}
            isUserSuperAdmin={isUserSuperAdmin}
            isMultiLocation={isMultiLocation}
            {...permissionsProps}
          />
        ))}
        <Box className="buttonFooter">
          <Button variant="outlined" color="secondary" onClick={handleClose}>
            CANCEL
          </Button>
          <Button
            disabled={!getIsFormDirty()}
            onClick={handleConfirm}
            isLoading={isSettingPermissions || isAddingUser}
          >
            {submitLabel}
          </Button>
        </Box>
      </StyledModal>
      <ConfirmCoordinatorModal
        open={isCoordinatorConfirmModalOpen}
        onConfirm={handleSubmit}
        onClose={() => setIsCoordinatorConfirmModalOpen(false)}
        isLoading={isSettingPermissions || isAddingUser}
      />
      <ConfirmSuperAdminModal
        open={isSuperAdminConfirmModalOpen}
        description="Super Admin users have the keys to the kingdom."
        onClose={() => setIsSuperAdminConfirmModalOpen(false)}
        onConfirm={() => {
          setIsSuperAdmin(true);
          setIsSuperAdminConfirmModalOpen(false);
        }}
      />
      <AssignUserToLocationsGroupsModal
        open={isAssignModalOpen}
        onClose={handleCloseAssign}
        onSubmit={handleAssignSubmit}
        isLoading={isAddingUser}
        firstName={newUser?.firstName}
        lastName={newUser?.lastName}
        isSuccess={isAssignSuccess}
        isError={isAssignError}
        reset={resetAssignUser}
        isNewUser
      />
    </>
  );
};

export default PermissionsModal;
